'use client';

/**
 * UpdatePrompt — tells the user a newer version exists and lets them take it
 * now or be reminded later.
 *
 * THREE sources, because "newer" means different things per platform:
 *
 *   WEB     → /version.json (auto-generated each deploy). A newer web deploy is
 *             instantly available: the action is just a reload.
 *
 *   ANDROID → Google Play In-App Updates API (@capawesome/capacitor-app-update).
 *             Asks the Play Store what version is available to THIS user on
 *             THEIR track (internal / closed / production) and downloads/installs
 *             it in-app. No manual version file to maintain, and no risk of
 *             pointing users at a track they aren't on — the Store is the source
 *             of truth. Only works for Play-installed builds (throws on
 *             debug/sideload, which we swallow). Replaced the old
 *             native-version.json path, which never fired because the file was
 *             parked at the public release and every tester ran ahead of it.
 *
 *   iOS     → /native-version.json (MANUALLY maintained). Apple has NO in-app
 *             update API, so this stays: the file records what is actually live
 *             on the App Store, and we prompt (→ store link) only when they can
 *             really get it. `"ios": "0.0.0"` = never prompt (nothing public yet).
 *
 * CORS note (iOS + web): native runs on `capacitor://localhost`, so hitting the
 * version file is cross-origin — firebase.json serves version.json /
 * native-version.json with `Access-Control-Allow-Origin: *` + no-cache.
 *
 * "Later" SNOOZES rather than dismissing forever. It reappears after the snooze
 * window — short on web (a reload is trivial), long on native (a store trip is not).
 */

import { useEffect, useState } from 'react';
import { RefreshCw, X, ExternalLink } from 'lucide-react';
import { APP_VERSION } from '@/lib/appVersion';

const POLL_INTERVAL_MS = 10 * 60 * 1000;          // re-check every 10 min
const SNOOZE_MS_WEB = 60 * 60 * 1000;             // 1 h  — reloading is cheap
const SNOOZE_MS_NATIVE = 12 * 60 * 60 * 1000;     // 12 h — don't nag for a store trip
const SNOOZE_KEY = 'opmode_update_snooze';        // { version, until }

const WEB_VERSION_URL = '/version.json';
const NATIVE_VERSION_URL = 'https://opmode.app/native-version.json';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.opmode.tactical';
const APP_STORE_URL = 'https://apps.apple.com/app/id6505632062';

type Platform = 'web' | 'ios' | 'android';

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(n => parseInt(n, 10));
  const pb = b.split('.').map(n => parseInt(n, 10));
  for (let i = 0; i < 3; i++) {
    const ai = pa[i] ?? 0;
    const bi = pb[i] ?? 0;
    if (ai !== bi) return ai - bi;
  }
  return 0;
}

export default function UpdatePrompt() {
  const [available, setAvailable] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform>('web');

  useEffect(() => {
    let cancelled = false;

    // A version is snoozed if the user hit "Later" and the window hasn't lapsed.
    // Once it lapses, the next poll re-surfaces the prompt (the "later" behavior).
    function isSnoozed(version: string): boolean {
      try {
        const raw = window.localStorage.getItem(SNOOZE_KEY);
        if (!raw) return false;
        const s = JSON.parse(raw) as { version?: string; until?: number };
        return s?.version === version && typeof s.until === 'number' && Date.now() < s.until;
      } catch { return false; }
    }

    async function check() {
      try {
        const { Capacitor } = await import('@capacitor/core').catch(() => ({ Capacitor: null as any }));
        const native: boolean = Capacitor?.isNativePlatform?.() ?? false;
        const plat: Platform = native
          ? (Capacitor.getPlatform() === 'ios' ? 'ios' : 'android')
          : 'web';
        if (cancelled) return;
        setPlatform(plat);

        // ANDROID → Google Play In-App Updates. The Store reports what's available
        // to this user on their own track; no version file, no track guesswork.
        if (plat === 'android') {
          try {
            const { AppUpdate, AppUpdateAvailability, FlexibleUpdateInstallStatus } =
              await import('@capawesome/capacitor-app-update');
            const info = await AppUpdate.getAppUpdateInfo();
            if (cancelled) return;
            const updatable = info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE;
            const readyToInstall = info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED;
            if (!updatable && !readyToInstall) { setAvailable(null); return; }
            const ver = info.availableVersionName
              || (info.availableVersionCode ? String(info.availableVersionCode) : 'update');
            setAvailable(isSnoozed(ver) ? null : ver);
          } catch {
            // Not a Play-installed build (debug/sideload) or plugin unavailable.
          }
          return;
        }

        // iOS + WEB → version-file comparison (iOS has no in-app update API).
        const url = plat === 'ios'
          ? `${NATIVE_VERSION_URL}?t=${Date.now()}`
          : `${WEB_VERSION_URL}?t=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const latest: string | undefined = plat === 'ios' ? data?.ios : data?.version;
        if (!latest || typeof latest !== 'string') return;

        // Nothing newer → clear any stale banner.
        if (compareVersions(latest, APP_VERSION) <= 0) { setAvailable(null); return; }
        setAvailable(isSnoozed(latest) ? null : latest);
      } catch {
        // Network blip / offline — try again next interval.
      }
    }

    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!available) return null;

  const storeUrl = platform === 'ios' ? APP_STORE_URL : PLAY_STORE_URL;
  // iOS sends the user out to the App Store; android + web update in place.
  const PrimaryIcon = platform === 'ios' ? ExternalLink : RefreshCw;
  const bodyCopy = platform === 'android'
    ? `Version ${available} is ready. Update now installs it in-app.`
    : platform === 'ios'
    ? `Version ${available} is available in the App Store.`
    : `Version ${available} is live. Reload to pick it up.`;

  const onUpdateNow = () => {
    if (platform === 'web') { window.location.reload(); return; }

    // ANDROID → run the Play In-App Update flow (download + install in-app).
    if (platform === 'android') {
      (async () => {
        try {
          const { AppUpdate, FlexibleUpdateInstallStatus } = await import('@capawesome/capacitor-app-update');
          const info = await AppUpdate.getAppUpdateInfo();
          // A prior flexible download is already sitting ready → just install.
          if (info.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
            await AppUpdate.completeFlexibleUpdate();
            return;
          }
          if (info.immediateUpdateAllowed) {
            await AppUpdate.performImmediateUpdate();     // Google's full-screen flow
            return;
          }
          if (info.flexibleUpdateAllowed) {
            const handle = await AppUpdate.addListener('onFlexibleUpdateStateChange', async (s) => {
              if (s.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
                try { await AppUpdate.completeFlexibleUpdate(); } catch { /* user can retry */ }
                try { await handle.remove(); } catch { /* noop */ }
              }
            });
            await AppUpdate.startFlexibleUpdate();
            return;
          }
          await AppUpdate.openAppStore();                 // last resort — open the listing
        } catch {
          try {
            const { AppUpdate } = await import('@capawesome/capacitor-app-update');
            await AppUpdate.openAppStore();
          } catch { /* nothing else we can do */ }
        }
      })();
      return;
    }

    // iOS → open the App Store listing.
    (async () => {
      try {
        const mod: any = await import('@capacitor/browser').catch(() => null);
        if (mod?.Browser?.open) { await mod.Browser.open({ url: storeUrl }); return; }
      } catch { /* fall through */ }
      window.open(storeUrl, '_blank', 'noopener');
    })();
  };

  const onLater = () => {
    const ms = platform !== 'web' ? SNOOZE_MS_NATIVE : SNOOZE_MS_WEB;
    try {
      window.localStorage.setItem(SNOOZE_KEY, JSON.stringify({ version: available, until: Date.now() + ms }));
    } catch { /* private mode — soft-hide for this session */ }
    setAvailable(null);
  };

  return (
    <div
      role="status"
      className="fixed bottom-20 left-3 right-3 sm:left-auto sm:right-4 sm:max-w-md z-40 rounded-xl border border-amber-500/40 bg-hud-ink/95 backdrop-blur-md shadow-[0_0_40px_-12px_rgba(245,158,11,0.5)] overflow-hidden animate-mode-in"
    >
      <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/80 to-transparent" />
      <div className="px-3.5 py-3">
        <div className="flex items-start gap-3">
          <PrimaryIcon className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400">
              Update available
            </div>
            <div className="font-mono text-[11px] text-slate-300 mt-0.5">
              {bodyCopy}
            </div>
          </div>
          <button
            onClick={onLater}
            aria-label="Remind me later"
            className="text-slate-500 hover:text-slate-300 p-1 -m-1 flex-shrink-0 transition-colors duration-fast"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={onLater}
            className="flex-1 px-3 py-2 rounded-md border border-operator-steel/50 text-slate-300 font-mono text-[10px] font-bold uppercase tracking-[0.18em] hover:border-slate-400 hover:text-white transition-colors duration-fast press"
          >
            Later
          </button>
          <button
            onClick={onUpdateNow}
            className="flex-[1.4] px-3 py-2 rounded-md bg-amber-500 text-operator-void font-mono text-[10px] font-bold uppercase tracking-[0.18em] hover:bg-amber-400 transition-colors duration-fast press"
          >
            Update now
          </button>
        </div>
      </div>
    </div>
  );
}
